// Shared weekly update viewer: date sidebar + square clickable images (lightbox, no new tab)
import React, { useMemo, useState } from "react";
import {
  CalendarDays, ChevronRight, MessageSquare, CheckCircle2, Eye,
} from "lucide-react";
import ImageModal from "../../../components/common/ImageModal";

export const fmtWeek = (dateStr) => {
  if (!dateStr) return "—";
  const start = new Date(dateStr);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const fmt = (d) =>
    d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  return `${fmt(start)} – ${fmt(end)}`;
};

export const fmtDateShort = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getUpdatePhotos = (update) => [
  { label: "Interior 1", url: update.interior_photo_1, group: "Interior" },
  { label: "Interior 2", url: update.interior_photo_2, group: "Interior" },
  { label: "Exterior 1", url: update.exterior_photo_1, group: "Exterior" },
  { label: "Exterior 2", url: update.exterior_photo_2, group: "Exterior" },
].filter((p) => p.url);

const SquareImageTile = ({ photo, onView }) => (
  <button
    type="button"
    onClick={() => onView(photo.url)}
    className="group relative w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden border border-gray-200 bg-gray-50 shrink-0"
    title={`View ${photo.label}`}
  >
    <img src={photo.url} alt={photo.label} className="w-full h-full object-cover" />
    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
      <ChevronRight size={18} className="text-white" />
    </div>
    <span className="absolute bottom-0 left-0 right-0 bg-black/55 text-[9px] text-white px-1 py-0.5 truncate">
      {photo.label}
    </span>
  </button>
);

const WeeklyUpdateDetailPanel = ({ update, accent = "indigo" }) => {
  const [lightboxImage, setLightboxImage] = useState(null);
  const photos = getUpdatePhotos(update);
  const comments = [1, 2, 3, 4, 5]
    .map((i) => update[`comment_${i}`])
    .filter(Boolean);

  const lightboxImages = useMemo(
    () => photos.map((p, i) => ({ src: p.url, id: `${update.id}-${i}` })),
    [photos, update.id]
  );

  const interior = photos.filter((p) => p.group === "Interior");
  const exterior = photos.filter((p) => p.group === "Exterior");

  const accentBg =
    accent === "blue" ? "bg-blue-50/60" : accent === "emerald" ? "bg-emerald-50/60" : "bg-indigo-50/60";
  const accentText =
    accent === "blue" ? "text-blue-600" : accent === "emerald" ? "text-emerald-600" : "text-indigo-600";

  return (
    <>
      {lightboxImage && (
        <ImageModal
          images={lightboxImages}
          selectedImage={lightboxImage}
          setSelectedImage={setLightboxImage}
          immersive
        />
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden h-full">
        <div className={`px-4 py-3 border-b border-gray-100 ${accentBg} flex items-center justify-between gap-2`}>
          <div className="flex items-center gap-2 min-w-0">
            <CalendarDays size={16} className={accentText} />
            <div className="min-w-0">
              <p className="font-bold text-sm text-gray-800 truncate">{fmtWeek(update.week_start_date)}</p>
              <p className="text-[10px] text-gray-500">Week start: {fmtDateShort(update.week_start_date)}</p>
            </div>
          </div>
          {update.submitted_at && (
            <span className="text-[10px] text-gray-500 flex items-center gap-1 shrink-0">
              <CheckCircle2 size={12} className="text-emerald-500" />
              {new Date(update.submitted_at).toLocaleDateString("en-GB")}
            </span>
          )}
        </div>

        <div className="p-4 space-y-4">
          {interior.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-gray-500 uppercase mb-2">Interior</p>
              <div className="flex flex-wrap gap-2">
                {interior.map((p) => (
                  <SquareImageTile key={p.label} photo={p} onView={setLightboxImage} />
                ))}
              </div>
            </div>
          )}

          {exterior.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-gray-500 uppercase mb-2">Exterior</p>
              <div className="flex flex-wrap gap-2">
                {exterior.map((p) => (
                  <SquareImageTile key={p.label} photo={p} onView={setLightboxImage} />
                ))}
              </div>
            </div>
          )}

          {comments.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-gray-500 uppercase mb-2 flex items-center gap-1">
                <MessageSquare size={11} /> Progress Points
              </p>
              <ul className="space-y-1">
                {comments.map((c, i) => (
                  <li key={i} className="flex gap-2 text-sm text-gray-700">
                    <span className={`${accentText} font-bold shrink-0`}>{i + 1}.</span>
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const WeeklyUpdateSidebarView = ({
  updates,
  selectedUpdateId,
  onSelectUpdate,
  accent = "indigo",
  emptyMessage = "No weekly updates yet.",
}) => {
  const selected = updates.find((u) => u.id === selectedUpdateId) || null;

  const accentActive =
    accent === "blue"
      ? "bg-blue-50 border-blue-300 text-blue-700"
      : accent === "emerald"
        ? "bg-emerald-50 border-emerald-300 text-emerald-700"
        : "bg-indigo-50 border-indigo-300 text-indigo-700";

  if (updates.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-dashed border-gray-200 p-8 text-center text-gray-400 text-sm">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row gap-3 min-h-[280px]">
      {/* Date sidebar */}
      <aside className="md:w-52 shrink-0 bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Available Dates</p>
        </div>
        <div className="p-2 space-y-1 max-h-[360px] overflow-y-auto">
          {updates.map((u) => {
            const active = selectedUpdateId === u.id;
            return (
              <button
                key={u.id}
                type="button"
                onClick={() => onSelectUpdate(u.id)}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-left text-xs font-semibold transition ${
                  active ? accentActive : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                }`}
              >
                <span>{fmtDateShort(u.week_start_date)}</span>
                <ChevronRight size={14} className={active ? "opacity-100" : "opacity-40"} />
              </button>
            );
          })}
        </div>
      </aside>

      {/* Detail panel */}
      <div className="flex-1 min-w-0">
        {selected ? (
          <WeeklyUpdateDetailPanel update={selected} accent={accent} />
        ) : (
          <div className="bg-white rounded-xl border border-dashed border-gray-200 h-full min-h-[240px] flex flex-col items-center justify-center text-center p-6 text-gray-400">
            <Eye size={28} className="mb-2 opacity-50" />
            <p className="text-sm font-semibold text-gray-500">Select a date to view update</p>
            <p className="text-xs mt-1">Click any date from the sidebar</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WeeklyUpdateSidebarView;
